/**
 * 动态分包加载失败恢复模块
 */

const CHUNK_RECOVERY_GUARD_KEY = 'ink_chunk_recovery_once_ts';
const CHUNK_RECOVERY_GUARD_TTL_MS = 3 * 60 * 1000;

function getErrorText(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (typeof error.message === 'string' && error.message) return error.message;
  if (typeof error.reason === 'string' && error.reason) return error.reason;
  return String(error);
}

function isLikelyChunkLoadError(error) {
  const text = getErrorText(error);
  if (!text) return false;

  return /ChunkLoadError/i.test(text) ||
    /Loading chunk [\w-]+ failed/i.test(text) ||
    /Failed to fetch dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text) ||
    /error loading dynamically imported module/i.test(text);
}

export function createChunkRecovery(context = {}) {
  const {
    showToast = () => {}
  } = context;

  let chunkRecoveryTriggered = false;

  function canAttemptChunkRecovery() {
    if (chunkRecoveryTriggered) return false;
    try {
      const raw = sessionStorage.getItem(CHUNK_RECOVERY_GUARD_KEY);
      const lastTs = Number(raw);
      if (Number.isFinite(lastTs) && Date.now() - lastTs < CHUNK_RECOVERY_GUARD_TTL_MS) {
        return false;
      }
    } catch (e) {
    }
    return true;
  }

  function markChunkRecoveryAttempt() {
    chunkRecoveryTriggered = true;
    try {
      sessionStorage.setItem(CHUNK_RECOVERY_GUARD_KEY, String(Date.now()));
    } catch (e) {
    }
  }

  async function triggerChunkLoadRecovery(error) {
    if (!isLikelyChunkLoadError(error)) return false;
    if (!canAttemptChunkRecovery()) return false;

    markChunkRecoveryAttempt();
    try {
      const regs = await navigator.serviceWorker?.getRegistrations?.();
      if (Array.isArray(regs)) {
        regs.forEach((reg) => {
          reg.update?.().catch(() => {});
        });
      }
    } catch (e) {
    }

    showToast('检测到应用资源更新，正在自动刷新...');
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('_reload', String(Date.now()));
    setTimeout(() => {
      window.location.replace(nextUrl.toString());
    }, 700);
    return true;
  }

  function importWithChunkRecovery(loader) {
    return Promise.resolve()
      .then(() => loader())
      .catch((error) => {
        triggerChunkLoadRecovery(error);
        throw error;
      });
  }

  function installChunkLoadRecoveryHandlers() {
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event?.reason;
      if (!isLikelyChunkLoadError(reason)) return;
      event.preventDefault?.();
      triggerChunkLoadRecovery(reason);
    });

    window.addEventListener('error', (event) => {
      const candidateError = event?.error || event?.message;
      if (isLikelyChunkLoadError(candidateError)) {
        triggerChunkLoadRecovery(candidateError);
        return;
      }

      const target = event?.target;
      const src = target?.src || target?.href || '';
      if (src && /\/assets\/.+\.(js|css)(\?|$)/i.test(src)) {
        triggerChunkLoadRecovery(new Error(`Asset load failed: ${src}`));
      }
    }, true);
  }

  return {
    importWithChunkRecovery,
    installChunkLoadRecoveryHandlers,
    triggerChunkLoadRecovery
  };
}
